import { Briefcase, Grid, Plus } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getWorkDashboard, getWorkTasks, getPublicWorkTasks } from '../api/workApi'
import { useAuth } from '../contexts/AuthContext'
import MainLayout from '../components/layout/MainLayout'
import WorkDashboardStats from '../components/work/WorkDashboardStats'
import WorkEmptyState from '../components/work/WorkEmptyState'
import WorkTaskCard from '../components/work/WorkTaskCard'
import type { WorkCategory, WorkDashboard, WorkTask, WorkTaskStatus } from '../types/work'

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
]

const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Categories' },
  { value: 'audio_processing', label: 'Audio' },
  { value: 'image_generation', label: 'Image' },
  { value: 'content_writing', label: 'Content' },
  { value: 'code', label: 'Code' },
  { value: 'data_analysis', label: 'Data' },
]

export default function Work() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [tasks, setTasks] = useState<WorkTask[]>([])
  const [myTasks, setMyTasks] = useState<WorkTask[]>([])
  const [dashboard, setDashboard] = useState<WorkDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [tab, setTab] = useState<'my' | 'all'>('my') // my work or all public work

  const loadData = useCallback(async () => {
    try {
      let tasksRes: WorkTask[] = []
      let myTasksRes: WorkTask[] = []
      let dashRes: WorkDashboard | null = null

      if (isAuthenticated) {
        const [myTr, allTr, dr] = await Promise.all([
          getWorkTasks({ status: statusFilter || undefined, category: categoryFilter || undefined }),
          getPublicWorkTasks({ category: categoryFilter || undefined }),
          getWorkDashboard(),
        ])
        myTasksRes = myTr
        tasksRes = allTr
        dashRes = dr
        setMyTasks(myTasksRes)
      } else {
        // Unauthenticated - show public tasks only
        tasksRes = await getPublicWorkTasks({ category: categoryFilter || undefined })
      }

      setTasks(tasksRes)
      setDashboard(dashRes)
    } catch {
      // silent
    } finally {
      setLoading(false)
    }
  }, [statusFilter, categoryFilter, isAuthenticated])

  useEffect(() => {
    void loadData()
  }, [loadData])

  function handleTaskRefresh(updated: WorkTask) {
    setTasks(prev => prev.map(t => (t.id === updated.id ? updated : t)))
  }

  // Select data based on tab
  const dataSource = isAuthenticated && tab === 'my' ? myTasks : tasks

  const filtered = dataSource.filter(t => {
    if (statusFilter && t.status !== (statusFilter as WorkTaskStatus)) {return false}
    if (categoryFilter && t.category !== (categoryFilter as WorkCategory)) {return false}
    return true
  })

  return (
    <MainLayout>
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Briefcase size={22} style={{ color: 'var(--primary, #6366f1)' }} />
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)' }}>Work</h1>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted, #6b7280)' }}>
              AI-powered one-time jobs
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => navigate('/work/categories')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '8px',
              background: 'transparent',
              border: '1px solid var(--border-color)',
              color: 'var(--text-secondary)',
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <Grid size={14} />
            Categories
          </button>
          <button
            onClick={() => navigate('/work/create')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: '8px',
              background: 'var(--primary, #6366f1)',
              color: '#fff',
              border: 'none',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <Plus size={14} />
            New Job
          </button>
        </div>
      </div>

      {/* Dashboard stats */}
      {dashboard && <div style={{ marginBottom: '24px' }}><WorkDashboardStats stats={dashboard} /></div>}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{
            padding: '8px 14px',
            borderRadius: '8px',
            background: 'var(--body-bg)',
            border: '1px solid var(--border-color)',
            color: 'var(--text-primary)',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            minWidth: '130px',
            outline: 'none',
          }}
        >
          {STATUS_OPTIONS.map(o => (
            <option key={o.value} value={o.value} style={{ background: 'var(--body-bg)', color: 'var(--text-primary)' }}>{o.label}</option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          style={{
            padding: '8px 14px',
            borderRadius: '8px',
            background: 'var(--body-bg)',
            border: '1px solid var(--border-color)',
            color: 'var(--text-primary)',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            minWidth: '150px',
            outline: 'none',
          }}
        >
          {CATEGORY_OPTIONS.map(o => (
            <option key={o.value} value={o.value} style={{ background: 'var(--body-bg)', color: 'var(--text-primary)' }}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Tabs - My Work / All Work (only for authenticated) */}
      {isAuthenticated && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '1px solid var(--border-color)' }}>
          <button
            onClick={() => setTab('my')}
            style={{
              padding: '12px 16px',
              background: tab === 'my' ? 'transparent' : 'transparent',
              border: 'none',
              borderBottom: tab === 'my' ? '2px solid #6366f1' : 'none',
              color: tab === 'my' ? '#6366f1' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: tab === 'my' ? 600 : 500,
            }}
          >
            My Work
          </button>
          <button
            onClick={() => setTab('all')}
            style={{
              padding: '12px 16px',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === 'all' ? '2px solid #6366f1' : 'none',
              color: tab === 'all' ? '#6366f1' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: tab === 'all' ? 600 : 500,
            }}
          >
            All Work
          </button>
        </div>
      )}

      {/* Task list */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{ height: '100px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', animation: 'pulse 1.5s ease-in-out infinite' }} />
          ))}
          <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
        </div>
      ) : filtered.length === 0 ? (
        <WorkEmptyState onCreateNew={() => navigate('/work/create')} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
          {filtered.map(task => (
            <WorkTaskCard key={task.id} task={task} onRefresh={handleTaskRefresh} />
          ))}
        </div>
      )}
    </div>
    </MainLayout>
  )
}
